/**
 * ANGELtype — In-page collection filter v9
 * - Only active on collection pages (prevents product page breadcrumb bug)
 * - Fully neutralizes inline module scripts to prevent duplicate InfiniScroll
 */

(function () {
  // Only run on collection pages — if we're not on one, do nothing
  if (!window.location.pathname.match(/^\/collections\/[^?#]+$/)) return;

  const SELECTORS = {
    tabs:        '.listoftags a',
    listingPage: '.listing-page',
    hero:        '[id*="hero_text"]',
    breadcrumb:  'nav[aria-label="breadcrumbs"]',
    products:    'section.product',
  };

  const COLLECTION_RE = /^\/collections\/[^?#]+$/;

  const PASSTHROUGH_CONTAINERS = [
    '.enter-overlay',
    '.fp-container',
    '.fp-menu-container',
    '#ajaxPagination',
    'section.loadmore',
    '.pagination',
  ];

  let infiniInstance = null;
  let infiniURL = null; // cached after first discovery

  function getPathname(href) {
    try { return new URL(href, window.location.origin).pathname; }
    catch { return href; }
  }

  function isCollectionLink(href) {
    if (!href) return false;
    try {
      const url = new URL(href, window.location.origin);
      if (url.search) return false; // ignore ?page=2, ?sort_by=, etc.
      return url.origin === window.location.origin && COLLECTION_RE.test(url.pathname);
    } catch { return false; }
  }

  function isPassthrough(element) {
    return PASSTHROUGH_CONTAINERS.some(sel => element.closest(sel));
  }

  function setActiveStates(pathname) {
    document.querySelectorAll(SELECTORS.tabs).forEach(t => {
      const match = getPathname(t.getAttribute('href')) === pathname;
      t.classList.toggle('is-active', match);
      t.setAttribute('aria-current', match ? 'page' : 'false');
    });

    document.querySelectorAll('.sidebar-dropdown').forEach(dropdown => {
      const details = dropdown.querySelector('details');
      let hasActive = false;
      dropdown.querySelectorAll('ul li').forEach(li => {
        const a = li.querySelector('a');
        if (!a) return;
        const match = getPathname(a.getAttribute('href')) === pathname;
        li.classList.toggle('active', match);
        if (match) hasActive = true;
      });
      if (details) {
        hasActive ? details.setAttribute('open', '') : details.removeAttribute('open');
      }
    });

    document.querySelectorAll('.sidebar-menu > ul > li:not(.sidebar-dropdown) > a').forEach(a => {
      const match = getPathname(a.getAttribute('href')) === pathname;
      a.classList.toggle('cf-active', match);
    });
  }

  function injectStyles() {
    if (document.getElementById('cf-styles')) return;
    const s = document.createElement('style');
    s.id = 'cf-styles';
    s.textContent = `
      .sidebar-menu > ul > li > a.cf-active {
        font-weight: bold;
        position: relative;
      }
      .sidebar-menu > ul > li > a.cf-active::before {
        content: '↳';
        position: absolute;
        left: -1.2em;
        font-size: 0.9em;
        opacity: 0.7;
      }
    `;
    document.head.appendChild(s);
  }

  // Swap a DOM node, neutralizing module scripts so the browser
  // doesn't auto-execute them (we manage InfiniScroll manually)
  function swapNode(oldNode, newHTML) {
    const neutralized = newHTML.replace(
      /(<script[^>]*type=["']module["'][^>]*>)([\s\S]*?)(<\/script>)/gi,
      '<script type="text/disabled-module">$2</script>'
    );
    const temp = document.createElement('div');
    temp.innerHTML = neutralized;
    const newNode = temp.firstElementChild;
    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.remove();
    return newNode;
  }

  function reRunNonModuleScripts(node) {
    node.querySelectorAll('script:not([type="text/disabled-module"])').forEach(old => {
      const fresh = document.createElement('script');
      [...old.attributes].forEach(attr => fresh.setAttribute(attr.name, attr.value));
      fresh.textContent = old.textContent;
      old.parentNode.replaceChild(fresh, old);
    });
  }

  function findInfiniScrollURL() {
    // Return cached URL immediately if we already found it
    if (infiniURL) return infiniURL;

    // 1. Check for a script tag with src containing infiniscroll
    const existing = document.querySelector('script[src*="infiniscroll"]');
    if (existing) { infiniURL = existing.src; return infiniURL; }

    // 2. Parse from any script tag (module, disabled-module, or plain)
    //    that contains an import from infiniscroll
    const allScripts = document.querySelectorAll('script');
    for (const s of allScripts) {
      const match = s.textContent.match(/from\s+['"]([^'"]*infiniscroll[^'"]*)['"]/);
      if (match) { infiniURL = match[1]; return infiniURL; }
    }

    return null;
  }

  async function startInfiniScroll() {
    if (infiniInstance) {
      try { await infiniInstance.stop(); } catch (e) {}
      infiniInstance = null;
    }

    // Retry up to 10 times (500ms total) waiting for DOM nodes to appear
    let container, pagination;
    for (let i = 0; i < 10; i++) {
      container  = document.querySelector('#ajaxSection');
      pagination = document.querySelector('#ajaxPagination');
      if (container && pagination) break;
      await new Promise(r => setTimeout(r, 50));
    }
    if (!container || !pagination) {
      // No pagination needed (collection fits on one page)
      return;
    }

    const url = findInfiniScrollURL();
    if (!url) { console.warn('[collection-filter] InfiniScroll URL not found'); return; }

    try {
      const { InfiniScroll } = await import(url);
      infiniInstance = InfiniScroll({
        itemsContainerSelector:      '#ajaxSection',
        paginationContainerSelector: '#ajaxPagination',
        loadingText: 'loading…',
        debug: false,
      });
      await infiniInstance.start();
    } catch (err) {
      console.error('[collection-filter] InfiniScroll init failed:', err);
    }
  }

  let isFetching = false;

  async function loadCollection(href, pushState = true) {
    if (isFetching) return;
    isFetching = true;

    const pathname = getPathname(href);

    const productSection = document.querySelector(SELECTORS.products);
    if (productSection) productSection.style.opacity = '0.35';

    try {
      const res = await fetch(pathname, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // 1. Swap entire listing-page
      const oldListing = document.querySelector(SELECTORS.listingPage);
      const newListing = doc.querySelector(SELECTORS.listingPage);
      if (oldListing && newListing) {
        // Save tab bar scroll position before swap
        const tabScroller = oldListing.querySelector('.listoftags .mob-scroller');
        const savedScrollLeft = tabScroller ? tabScroller.scrollLeft : 0;

        const freshListing = swapNode(oldListing, newListing.outerHTML);
        reRunNonModuleScripts(freshListing);
        const freshProducts = freshListing.querySelector(SELECTORS.products);
        if (freshProducts) freshProducts.style.opacity = '1';

        // Tab scroll position restored after setActiveStates (see below)
      }

      // 2. Swap hero
      const heroSection    = document.querySelector(SELECTORS.hero);
      const newHeroSection = doc.querySelector(SELECTORS.hero);
      if (heroSection && newHeroSection) {
        const freshHero = swapNode(heroSection, newHeroSection.outerHTML);
        reRunNonModuleScripts(freshHero);
      }

      // 3. Breadcrumbs
      const crumb    = document.querySelector(SELECTORS.breadcrumb);
      const newCrumb = doc.querySelector(SELECTORS.breadcrumb);
      if (crumb && newCrumb) crumb.innerHTML = newCrumb.innerHTML;

      // 4. Page title
      const newTitle = doc.querySelector('title');
      if (newTitle) document.title = newTitle.textContent;

      // 5. History
      if (pushState) window.history.pushState({ collection: pathname }, '', pathname);

      // 6. Scroll
      setTimeout(() => {
        if (window.innerWidth >= 769) {
          const crumbEl = document.querySelector(SELECTORS.breadcrumb);
          if (crumbEl) {
            const top = crumbEl.getBoundingClientRect().top + window.scrollY - 8;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 80);

      // 7. Set active states on the newly swapped tab bar, then position scroller
      setActiveStates(pathname);
      const freshScroller = document.querySelector('.listoftags .mob-scroller');
      const activeTab = freshScroller && freshScroller.querySelector('.is-active');
      if (freshScroller && activeTab) {
        // Only scroll if the active tab is outside the visible area of the scroller
        const scrollerLeft  = freshScroller.scrollLeft;
        const scrollerWidth = freshScroller.offsetWidth;
        const tabLeft       = activeTab.offsetLeft;
        const tabRight      = tabLeft + activeTab.offsetWidth;
        const isVisible     = tabLeft >= scrollerLeft && tabRight <= scrollerLeft + scrollerWidth;
        if (!isVisible) {
          // Scroll just enough to show the tab — align its left edge with a small margin
          freshScroller.style.scrollBehavior = 'auto';
          freshScroller.scrollLeft = tabLeft - 16;
          freshScroller.style.scrollBehavior = '';
        }
      }

      // 8. Re-init InfiniScroll — wait for DOM to settle after swap
      await new Promise(r => setTimeout(r, 50));
      await startInfiniScroll();

      document.dispatchEvent(new CustomEvent('collection:changed', { detail: { pathname } }));

    } catch (err) {
      console.error('[collection-filter]', err);
      window.location.href = pathname;
    } finally {
      isFetching = false;
    }
  }

  function closeMobileMenu() {
    // Bullet controls menu visibility via 'open-menu' class on body
    document.body.classList.remove('open-menu');
    // Also collapse any open details inside the menu
    document.querySelectorAll('#mobmenu details[open]').forEach(d => d.removeAttribute('open'));
  }

  function handleClick(e) {
    const link = e.target.closest('a');
    if (!link) return;
    if (isPassthrough(link)) return;
    const href = link.getAttribute('href');
    if (!isCollectionLink(href)) return;
    e.preventDefault();
    closeMobileMenu();
    loadCollection(href);
  }

  async function init() {
    injectStyles();
    document.addEventListener('click', handleClick);

    window.addEventListener('popstate', (e) => {
      const path = e.state?.collection || window.location.pathname;
      if (COLLECTION_RE.test(path)) loadCollection(path, false);
    });

    // Set initial active state on page load (before any swap)
    setActiveStates(window.location.pathname);

    // Cache the InfiniScroll URL now while the original type="module" script
    // is still in the DOM — but don't start our own instance on first load.
    // The page's inline module script already handles the initial collection.
    // We take over only after the first swap (loadCollection calls startInfiniScroll).
    findInfiniScrollURL();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
})();